import { createContext, useContext } from "react";
import { useAccount } from "../../../generic/hooks/use-account";
import { Draggable } from "../../../moment-card/components/drag-and-drop/moment-card-draggable";
import { MomentCardDropArea } from "../../../moment-card/components/drag-and-drop/moment-card-drop-area";

import type { UUID } from "../../../../utils/uuid";
import type { Account } from "../../../generic/server/supabase-account-service";
import type { FC, ReactNode } from "react";

/** The account Communications are dragged from; loaded once, for every row. */
const FeedAccount = createContext<Pick<Account, "id" | "name"> | undefined>(undefined);

/** Lets the Communications inside be dragged to another account's home page, to copy them there. */
export const DraggableCommunications: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: account } = useAccount();

  return <FeedAccount.Provider value={account ?? undefined}>{children}</FeedAccount.Provider>;
};

/**
 * Copies a Communication dragged here from another account's home page, as the original home page
 * did, and lets those in the feed be dragged to another's.
 */
export const FeedDragAndDrop: FC<{
  children: ReactNode;
  /** A Communication dropped here has been copied. */
  onCopied: () => void;
}> = ({ children, onCopied }) => (
  <MomentCardDropArea onCopied={onCopied}>
    <DraggableCommunications>{children}</DraggableCommunications>
  </MomentCardDropArea>
);

/** A Communication that can be dragged to another account's home page, inside DraggableCommunications. */
export const DraggableCommunication: FC<{ id: UUID; children: ReactNode }> = ({ id, children }) => (
  <Draggable account={useContext(FeedAccount)} momentId={id}>
    {children}
  </Draggable>
);
